import React, { useEffect, useMemo, useState } from "react";
import { Drawer, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import theme from "../../theme";
import CommonInput from "../input";
import CommonFormDropDown from "../formdropdown";
import CommonSwitch from "../switch";
import CommonButton from "../button";
import { CATEGORY_OPTIONS, isoToLocalInput, localInputToIso } from "./utils";

const useStyles = makeStyles(() => ({
  paper: {
    width: "460px",
    maxWidth: "100vw",
    padding: "28px 32px 40px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  title: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: "22px",
  },
  subtitle: {
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "13px",
    marginBottom: "24px",
  },
  label: {
    fontSize: "16px",
    color: theme.colors.Violet,
    marginBottom: "5px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.colors.Zumthor,
    borderRadius: "6px",
    padding: "6px 14px",
    marginBottom: "20px",
  },
  dateGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "14px",
    marginBottom: "20px",
  },
  dateInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px",
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Violet,
    backgroundColor: theme.colors.Zumthor,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "4px",
    outline: "none",
  },
  fieldError: {
    color: theme.colors.Pomegranate,
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    marginTop: "-14px",
    marginBottom: "16px",
  },
  actions: {
    display: "flex",
    gap: "12px",
    marginTop: "12px",
  },
}));

const emptyForm = {
  name: "",
  category: "SIGNUP",
  description: "",
  amount: "",
  budget_limit: "",
  is_active: true,
  starts_at: "",
  ends_at: "",
};

// Pull a server-side field error (details map: { field: [msg] }) for a field.
const serverError = (fieldErrors, key) => {
  const val = fieldErrors?.[key];
  if (!val) return "";
  if (Array.isArray(val)) return val.filter(Boolean).join(" ");
  if (typeof val === "string") return val;
  return "";
};

function BonusFormDrawer({
  open,
  mode = "create",
  bonus,
  loading = false,
  fieldErrors,
  onSubmit,
  onClose,
}) {
  const classes = useStyles();
  const [form, setForm] = useState(emptyForm);
  const [clientErrors, setClientErrors] = useState({});

  // Sync form when opening / switching the bonus being edited.
  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && bonus) {
      setForm({
        name: bonus.name || "",
        category: bonus.category || "SIGNUP",
        description: bonus.description || "",
        amount: bonus.amount ?? "",
        budget_limit:
          bonus.budget_limit === null || bonus.budget_limit === undefined
            ? ""
            : bonus.budget_limit,
        is_active: bonus.is_active ?? true,
        starts_at: isoToLocalInput(bonus.starts_at),
        ends_at: isoToLocalInput(bonus.ends_at),
      });
    } else {
      setForm(emptyForm);
    }
    setClientErrors({});
  }, [open, mode, bonus]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const categoryValue = useMemo(
    () => CATEGORY_OPTIONS.find((c) => c.value === form.category) || null,
    [form.category]
  );

  const validate = () => {
    const errors = {};
    if (!form.name || !form.name.trim()) {
      errors.name = "Name is required.";
    }
    const amountNum = Number(form.amount);
    if (form.amount === "" || Number.isNaN(amountNum)) {
      errors.amount = "Amount is required.";
    } else if (amountNum < 1) {
      errors.amount = "Amount must be at least 1.";
    }
    if (form.budget_limit !== "" && Number(form.budget_limit) < 0) {
      errors.budget_limit = "Budget limit cannot be negative.";
    }
    if (form.starts_at && form.ends_at) {
      if (new Date(form.starts_at).getTime() >= new Date(form.ends_at).getTime()) {
        errors.ends_at = "End must be after start.";
      }
    }
    setClientErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (loading) return;
    if (!validate()) return;
    const body = {
      name: form.name.trim(),
      category: form.category || "SIGNUP",
      description: form.description?.trim() ? form.description.trim() : null,
      amount: Number(form.amount),
      budget_limit:
        form.budget_limit === "" ? null : Number(form.budget_limit),
      is_active: !!form.is_active,
      starts_at: localInputToIso(form.starts_at),
      ends_at: localInputToIso(form.ends_at),
    };
    onSubmit(body);
  };

  // Combined error per field: client validation first, then server detail.
  const errFor = (key) => clientErrors[key] || serverError(fieldErrors, key);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={loading ? undefined : onClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div className={classes.title}>
          {mode === "edit" ? "Edit bonus" : "Create bonus"}
        </div>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.subtitle}>
        {mode === "edit"
          ? "Update the details of this bonus."
          : "Define a new bonus that eligible employees can redeem."}
      </div>

      <CommonInput
        label="Name"
        placeholder="e.g. Welcome bonus"
        value={form.name}
        onChange={(e) => setField("name", e.target.value)}
        error={!!errFor("name")}
        helperText={errFor("name")}
      />

      <div className={classes.label}>Category</div>
      <CommonFormDropDown
        placeholder="Select category"
        options={CATEGORY_OPTIONS}
        value={categoryValue}
        onChange={(opt) => setField("category", opt?.value || "SIGNUP")}
        error={!!errFor("category")}
        helperText={errFor("category")}
      />

      <CommonInput
        label="Description"
        placeholder="Optional description"
        multiline
        minRows={2}
        value={form.description}
        onChange={(e) => setField("description", e.target.value)}
        error={!!errFor("description")}
        helperText={errFor("description")}
      />

      <CommonInput
        label="Amount"
        placeholder="0.00"
        type="number"
        allowDecimal={2}
        onlyPositive
        min={1}
        value={form.amount}
        onChange={(e) => setField("amount", e.target.value)}
        error={!!errFor("amount")}
        helperText={errFor("amount")}
      />

      <CommonInput
        label="Budget limit (optional)"
        placeholder="Leave empty for uncapped"
        type="number"
        allowDecimal={2}
        onlyPositive
        min={0}
        value={form.budget_limit}
        onChange={(e) => setField("budget_limit", e.target.value)}
        error={!!errFor("budget_limit")}
        helperText={errFor("budget_limit")}
      />

      <div className={classes.switchRow}>
        <span className={classes.label} style={{ marginBottom: 0 }}>
          Active
        </span>
        <CommonSwitch
          checked={!!form.is_active}
          onChange={(e) => setField("is_active", e.target.checked)}
          inputProps={{ "aria-label": "Active" }}
        />
      </div>

      <div className={classes.dateGrid}>
        <div>
          <div className={classes.label}>Starts at (optional)</div>
          <input
            type="datetime-local"
            className={classes.dateInput}
            value={form.starts_at}
            onChange={(e) => setField("starts_at", e.target.value)}
            aria-label="Starts at"
          />
        </div>
        <div>
          <div className={classes.label}>Ends at (optional)</div>
          <input
            type="datetime-local"
            className={classes.dateInput}
            value={form.ends_at}
            onChange={(e) => setField("ends_at", e.target.value)}
            aria-label="Ends at"
          />
        </div>
      </div>
      {errFor("starts_at") ? (
        <div className={classes.fieldError} style={{ marginTop: "-12px" }}>
          {errFor("starts_at")}
        </div>
      ) : null}
      {errFor("ends_at") ? (
        <div className={classes.fieldError} style={{ marginTop: "-12px" }}>
          {errFor("ends_at")}
        </div>
      ) : null}

      <div className={classes.actions}>
        <CommonButton
          type="button"
          text="Cancel"
          stylevariant="secondary2"
          fontSize="15px"
          disabled={loading}
          onClick={onClose}
        />
        <CommonButton
          type="button"
          text={mode === "edit" ? "Save changes" : "Create bonus"}
          stylevariant="primary"
          fontSize="15px"
          loading={loading}
          disabled={loading}
          onClick={handleSubmit}
        />
      </div>
    </Drawer>
  );
}

export default BonusFormDrawer;
